import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

/* eslint-disable @typescript-eslint/no-namespace */
import ViewerStudyResults from "@src/modules/DicomViewer/ViewerStudyResults/ViewerStudyResults";
import ViewerWrapper from "@src/modules/DicomViewer/ViewerWrapper";
import { ContentWrapper } from "@src/modules/shared/components/components";
import ScreenLoading from "@src/modules/shared/components/ScreenLoading";
import useAppContext from "@src/modules/App/hooks/useAppContext";
import { DicomViewerState } from "@src/modules/DicomViewer/hooks/useDicomViewerVisibility";

type DicomViewerElementProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLElement>,
  HTMLElement
> & { studyId?: string };
declare global {
  namespace JSX {
    interface IntrinsicElements {
      "dicom-viewer": DicomViewerElementProps;
    }
  }
}

const DicomViewerComponent: FunctionComponent<{ studyId?: string }> = ({
  studyId,
}) => {
  return useMemo(() => <dicom-viewer studyId={studyId} />, [studyId]);
};

const DicomViewer: FunctionComponent = () => {
  const { dicomViewer } = useAppContext();
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (dicomViewer.state === DicomViewerState.NotInitialized) return;

    setIsLoading(!!!document.querySelector("#dicom-viewer"));

    const getLangCode = (): string => {
      const defaultLang = "en";
      const availableLangs = [defaultLang, "pl", "ru", "es", "fr"];
      const currentLang = i18n.language.split("-")[0];

      return availableLangs.indexOf(currentLang) > -1
        ? currentLang
        : defaultLang;
    };

    const script: HTMLScriptElement =
      document.querySelector("#dicom-viewer") ||
      document.createElement("script");
    script.id = "dicom-viewer";
    script.type = "text/javascript";
    script.src = `/ng-elements/dicom-viewer-element-${getLangCode()}.js?v=${uuidv4()}`;
    script.async = true;
    script.onload = () => setIsLoading(false);
    document.body.appendChild(script);
  }, [dicomViewer.state, i18n.language]);

  return (
    <ContentWrapper
      style={{
        display:
          dicomViewer.state === DicomViewerState.Visible ? "block" : "none",
      }}
    >
      {dicomViewer.studyId && (
        <ViewerStudyResults studyId={dicomViewer.studyId} />
      )}
      <ViewerWrapper>
        {isLoading ? (
          <ScreenLoading />
        ) : (
          <DicomViewerComponent studyId={dicomViewer.studyId} />
        )}
      </ViewerWrapper>
    </ContentWrapper>
  );
};

export default DicomViewer;
